/**
 * ToastManager - A vanilla JS toast notification system
 * Styled to match the dashboard app design
 * Toasts are persistent and must be dismissed by the user
 */

class ToastManager {
    constructor() {
        this.container = null;
        this.toasts = [];
        this.toastId = 0;
        this.init();
    }

    init() {
        // Create container if it doesn't exist
        if (!document.getElementById('toast-container')) {
            this.container = document.createElement('div');
            this.container.id = 'toast-container';
            this.container.setAttribute('aria-live', 'polite');
            this.container.setAttribute('aria-atomic', 'true');
            document.body.appendChild(this.container);
            this.injectStyles();
        } else {
            this.container = document.getElementById('toast-container');
        }
    }

    injectStyles() {
        if (document.getElementById('toast-manager-styles')) return;

        const styles = document.createElement('style');
        styles.id = 'toast-manager-styles';
        styles.textContent = `
            #toast-container {
                position: fixed;
                top: 20px;
                right: 20px;
                z-index: 10000;
                display: flex;
                flex-direction: column;
                gap: 12px;
                max-width: 400px;
                width: calc(100% - 40px);
                pointer-events: none;
            }

            @media (max-width: 480px) {
                #toast-container {
                    top: 10px;
                    right: 10px;
                    left: 10px;
                    width: auto;
                    max-width: none;
                }
            }

            .toast {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                padding: 16px 20px;
                border-radius: 10px;
                background-color: #ffffff;
                box-shadow: 0 8px 40px 0 rgba(0, 0, 0, 0.15);
                font-family: Roboto, Helvetica, Arial, sans-serif;
                font-size: 14px;
                line-height: 1.5;
                pointer-events: auto;
                animation: toastSlideIn 0.3s ease-out;
                border-left: 4px solid;
            }

            .toast.toast-removing {
                animation: toastSlideOut 0.3s ease-in forwards;
            }

            @keyframes toastSlideIn {
                from {
                    opacity: 0;
                    transform: translateX(100%);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            @keyframes toastSlideOut {
                from {
                    opacity: 1;
                    transform: translateX(0);
                }
                to {
                    opacity: 0;
                    transform: translateX(100%);
                }
            }

            .toast-icon {
                flex-shrink: 0;
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
            }

            .toast-icon svg {
                width: 14px;
                height: 14px;
            }

            .toast-content {
                flex: 1;
                min-width: 0;
            }

            .toast-title {
                font-weight: 600;
                font-size: 15px;
                color: #363b5d;
                margin-bottom: 4px;
            }

            .toast-message {
                color: #7b929f;
                word-wrap: break-word;
            }

            .toast-close {
                flex-shrink: 0;
                width: 28px;
                height: 28px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                border: none;
                cursor: pointer;
                color: #9098ac;
                border-radius: 50%;
                transition: all 0.2s ease;
                margin: -4px -8px -4px 0;
            }

            .toast-close:hover {
                background-color: #f5f8fa;
                color: #363b5d;
            }

            .toast-close svg {
                width: 16px;
                height: 16px;
            }

            /* Error/Danger state */
            .toast-danger {
                border-left-color: #dc3545;
            }

            .toast-danger .toast-icon {
                background-color: #fdeaea;
                color: #dc3545;
            }

            .toast-danger .toast-title {
                color: #dc3545;
            }

            /* Success state */
            .toast-success {
                border-left-color: #28a745;
            }

            .toast-success .toast-icon {
                background-color: #e8f5e9;
                color: #28a745;
            }

            .toast-success .toast-title {
                color: #28a745;
            }

            /* Warning state */
            .toast-warning {
                border-left-color: #ffc107;
            }

            .toast-warning .toast-icon {
                background-color: #fff8e1;
                color: #f57c00;
            }

            .toast-warning .toast-title {
                color: #f57c00;
            }

            /* Info state */
            .toast-info {
                border-left-color: #2f4ea7;
            }

            .toast-info .toast-icon {
                background-color: #e8eef7;
                color: #2f4ea7;
            }

            .toast-info .toast-title {
                color: #2f4ea7;
            }
        `;
        document.head.appendChild(styles);
    }

    getIcon(type) {
        const icons = {
            danger: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>`,
            success: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>`,
            warning: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>`,
            info: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>`
        };
        return icons[type] || icons.info;
    }

    getTitle(type) {
        const titles = {
            danger: 'Σφάλμα',
            success: 'Επιτυχία',
            warning: 'Προσοχή',
            info: 'Πληροφορία'
        };
        return titles[type] || titles.info;
    }

    /**
     * Show a toast notification
     * @param {string} message - The message to display
     * @param {string} type - The type of toast: 'danger', 'success', 'warning', 'info'
     * @param {object} options - Optional settings
     * @param {string} options.title - Custom title (overrides default)
     * @param {string} options.group - Group identifier for clearing specific toasts
     * @returns {number} Toast ID for programmatic removal
     */
    show(message, type = 'info', options = {}) {
        const id = ++this.toastId;
        const title = options.title || this.getTitle(type);
        const group = options.group || 'default';

        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        toast.setAttribute('role', 'alert');
        toast.setAttribute('data-toast-id', id);
        toast.setAttribute('data-toast-group', group);

        toast.innerHTML = `
            <div class="toast-icon">
                ${this.getIcon(type)}
            </div>
            <div class="toast-content">
                <div class="toast-title">${title}</div>
                <div class="toast-message">${message}</div>
            </div>
            <button class="toast-close" aria-label="Κλείσιμο" type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        `;

        const closeBtn = toast.querySelector('.toast-close');
        closeBtn.addEventListener('click', () => this.dismiss(id));

        this.container.appendChild(toast);
        this.toasts.push({ id, element: toast, group });

        return id;
    }

    /**
     * Dismiss a specific toast by ID
     * @param {number} id - The toast ID
     */
    dismiss(id) {
        const toastData = this.toasts.find(t => t.id === id);
        if (!toastData) return;

        const { element } = toastData;
        element.classList.add('toast-removing');

        element.addEventListener('animationend', () => {
            element.remove();
            this.toasts = this.toasts.filter(t => t.id !== id);
        });
    }

    /**
     * Clear all toasts, optionally filtered by group
     * @param {string} group - Optional group to clear
     */
    clear(group = null) {
        const toastsToClear = group
            ? this.toasts.filter(t => t.group === group)
            : [...this.toasts];

        toastsToClear.forEach(t => this.dismiss(t.id));
    }

    /**
     * Show an error toast
     * @param {string} message - Error message
     * @param {object} options - Optional settings
     */
    error(message, options = {}) {
        return this.show(message, 'danger', options);
    }

    /**
     * Show a success toast
     * @param {string} message - Success message
     * @param {object} options - Optional settings
     */
    success(message, options = {}) {
        return this.show(message, 'success', options);
    }

    /**
     * Show a warning toast
     * @param {string} message - Warning message
     * @param {object} options - Optional settings
     */
    warning(message, options = {}) {
        return this.show(message, 'warning', options);
    }

    /**
     * Show an info toast
     * @param {string} message - Info message
     * @param {object} options - Optional settings
     */
    info(message, options = {}) {
        return this.show(message, 'info', options);
    }
}

// Create singleton instance
const toastManager = new ToastManager();

// Export for ES modules
export default toastManager;

// Also export individual methods for convenience
export const toast = {
    show: (message, type, options) => toastManager.show(message, type, options),
    error: (message, options) => toastManager.error(message, options),
    success: (message, options) => toastManager.success(message, options),
    warning: (message, options) => toastManager.warning(message, options),
    info: (message, options) => toastManager.info(message, options),
    dismiss: (id) => toastManager.dismiss(id),
    clear: (group) => toastManager.clear(group)
};
